/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  useColorStyle,
  useColorMode,
  Text,
  SearchInput,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TableBody,
  TableCell,
  Table,
  Icon,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import React, { useState, useMemo, useEffect } from 'react';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { ConditionalAccessProps } from '../../../types/globalSetting';
import {
  flexRender,
  ColumnDef,
  useReactTable,
  getCoreRowModel,
} from '@tanstack/react-table';
import SmartTooltip from '../../../pages/Report/SmartTooltip';
import CSpinner from '../../../components/CSpinner';
import { useOrgStore } from '../../../models/useOrgModel';
import ConditionalAccessPolicyNameCell from './EditConditionalAccessPoliciesForRiksyUsers';
import { GlobalApi, ProvisionApi } from '@Apis';
import { Trans, useTranslation } from 'react-i18next';
import { AppType } from '../../../types/app';
import { THIRD_PARTY_LINK } from '@Utils/casDocLink';

const ConditionalAccessPoliciesForRiskyUsers = React.memo(() => {
  const { orgId } = useOrgStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [
    showConditionalAccessPoliciesForRiskyUsers,
    setShowConditionalAccessPoliciesForRiskyUsers,
  ] = useState<boolean>(false);
  const [items, setItems] = useState<ConditionalAccessProps[] | []>([]);
  const [allItems, setAllItems] = useState<ConditionalAccessProps[] | []>([]);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [refreshTrigger, setRefreshTrigger] = useState<null | Date>(null);
  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems.filter(
        (item) =>
          item.policyName.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, [orgId]);

  useEffect(() => {
    if (!refreshTrigger) {
      return;
    }
    loadData();
  }, [refreshTrigger]);

  async function loadData() {
    try {
      setLoading(true);
      const policyTypes = (await ProvisionApi.getProvisionedServices(orgId))
        .data;
      let data: ConditionalAccessProps[] = [];
      if (!policyTypes.includes(AppType.IdentityProtection)) {
        setShowConditionalAccessPoliciesForRiskyUsers(true);
      } else {
        setShowConditionalAccessPoliciesForRiskyUsers(false);
        data = reShapeData((await GlobalApi.getConditionalAccessPolicy()).data);
      }
      setItems(data);
      setAllItems(data);
    } catch (error) {
      setErrorMsg(`Error: ${error}`);
    } finally {
      setLoading(false);
    }
  }

  function reShapeData(data: ConditionalAccessProps[]) {
    data.forEach((row) => {
      if (row.users.length > 0) {
        let usersList = '';
        for (const user of row.users) {
          usersList = usersList + ', ' + user.userName;
        }
        usersList = usersList.slice(2);
        row.usersList = usersList;
      }
      if (row.action === 'block' && row.includeApp === 'all') {
        row.policyName = t(
          'globalSetting.conditionalAccess.policyName.BlockAll'
        );
        row.actionName = t('globalSetting.conditionalAccess.action.BlockAll');
        row.sort = 1;
      } else if (row.action === 'block' && row.includeApp === 'office365') {
        row.policyName = t(
          'globalSetting.conditionalAccess.policyName.Block365'
        );
        row.actionName = t('globalSetting.conditionalAccess.action.Block365');
        row.sort = 2;
      } else if (row.action === 'mfa' && row.includeApp === 'all') {
        row.policyName = t(
          'globalSetting.conditionalAccess.policyName.RequireAll'
        );
        row.actionName = t('globalSetting.conditionalAccess.action.RequireAll');
        row.sort = 3;
      } else if (row.action === 'mfa' && row.includeApp === 'office365') {
        row.policyName = t(
          'globalSetting.conditionalAccess.policyName.Require365'
        );
        row.actionName = t('globalSetting.conditionalAccess.action.Require365');
        row.sort = 4;
      }
    });
    data.sort(
      (a: ConditionalAccessProps, b: ConditionalAccessProps) => a.sort - b.sort
    );
    return data;
  }

  const columns: ColumnDef<ConditionalAccessProps>[] = useMemo(
    () => [
      {
        id: 'policy',
        header: t('globalSetting.conditionalAccess.table.policy'),
        accessorKey: 'policyName',
        size: 320,
        cell: ({ row }) => {
          return (
            <ConditionalAccessPolicyNameCell
              policyName={row.original.policyName}
              action={row.original.action}
              users={row.original.users}
              id={row.original.id}
              enabled={row.original.enabled}
              includeApp={row.original.includeApp}
              isDefault={row.original.isDefault}
              setRefreshTrigger={setRefreshTrigger}
            />
          );
        },
      },
      {
        id: 'action',
        header: t('globalSetting.conditionalAccess.table.action'),
        accessorKey: 'actionName',
        size: 350,
      },
      {
        id: 'targetUsers',
        header: t('globalSetting.conditionalAccess.table.targetUsers'),
        accessorKey: 'usersList',
        size: 310,
      },
    ],
    [items, allItems]
  );
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 100,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box px="4x">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      <Flex m="16px 0px">
        <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
          {t('globalSetting.conditionalAccess.title')}
        </Text>
      </Flex>
      {showConditionalAccessPoliciesForRiskyUsers && (
        <Flex m="16px 0px" align="center">
          <Icon as={InfoOIcon} color={colorStyle.color.info} />
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
            ml="8px"
          >
            {t('globalSetting.conditionalAccess.tips')}
          </Text>
        </Flex>
      )}
      <Flex m="12px 0px">
        <Text
          color={colorStyle.color.secondary}
          lineHeight="lg"
          maxWidth={1000}
        >
          {t('globalSetting.conditionalAccess.desc')}
        </Text>
      </Flex>
      <SearchInput
        mt="20px"
        placeholder={t('globalSetting.conditionalAccess.table.search')}
        onChange={(e: any) => {
          search(e.target.value);
        }}
        onClearInput={() => {
          search('');
        }}
        width="374px"
      />
      <Table mt={20} ml={16} layout="flexbox" width="1000px">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const styleProps = {
                  minWidth: header.column.columnDef.minSize,
                  width: header.getSize(),
                  ...header.column.columnDef.meta?.customColumnStyle,
                };
                return (
                  <Flex flexDirection="row">
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : header.id !== 'action' ? (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      ) : (
                        <Flex direction="row" align="center">
                          <SmartTooltip>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </SmartTooltip>
                          <Popover trigger="hover">
                            <PopoverTrigger>
                              <Icon ml="4px" as={InfoOIcon}></Icon>
                            </PopoverTrigger>
                            <PopoverContent maxWidth="384px" fontWeight={400}>
                              <Text>
                                {t(
                                  'globalSetting.conditionalAccess.table.actionContent1'
                                )}
                              </Text>
                              <Text>
                                <Trans i18nKey="globalSetting.conditionalAccess.table.actionContent2">
                                  placeholder
                                  <Link
                                    href={
                                      THIRD_PARTY_LINK.conditionalAccessAction
                                    }
                                    target="_blank"
                                  >
                                    placeholder
                                  </Link>
                                </Trans>
                              </Text>
                            </PopoverContent>
                          </Popover>
                        </Flex>
                      )}
                    </TableHeaderCell>
                  </Flex>
                );
              })}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <TableBody overflowY="auto">
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} height="40px">
              {row.getVisibleCells().map((cell) => {
                const styleProps = {
                  minWidth: cell.column.columnDef.minSize,
                  width: cell.column.getSize(),
                  ...cell.column.columnDef.meta?.customColumnStyle,
                };
                return (
                  <TableCell key={cell.id} {...styleProps}>
                    {cell.column.id !== 'policy' ? (
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
});
export default ConditionalAccessPoliciesForRiskyUsers;
